import { useState, useEffect, useCallback, useMemo } from 'react';
import { s, authHeaders, Toast, StandardDataGrid, gridActionButton } from './listPageStyles';

const API_BASE = '/api/notifications/admin';

const TYPE_TABS = [
  { key: '', label: 'Tümü' },
  { key: 'ORDER_STATUS', label: 'Sipariş' },
  { key: 'CAMPAIGN', label: 'Kampanya' },
  { key: 'TASK_ASSIGNED', label: 'Görev' },
  { key: 'POINT_OPENING', label: 'Nokta Açma' },
  { key: 'TRAINING', label: 'Eğitim' },
  { key: 'SYSTEM', label: 'Sistem' },
];

const TYPE_META = {
  ORDER_STATUS: { label: 'Sipariş', tone: 'blue' },
  CAMPAIGN: { label: 'Kampanya', tone: 'purple' },
  TASK_ASSIGNED: { label: 'Görev', tone: 'orange' },
  POINT_OPENING: { label: 'Nokta Açma', tone: 'green' },
  TRAINING: { label: 'Eğitim', tone: 'teal' },
  SYSTEM: { label: 'Sistem', tone: 'gray' },
};

const READ_TABS = [
  { key: '', label: 'Hepsi' },
  { key: 'false', label: 'Okunmamış' },
  { key: 'true', label: 'Okunmuş' },
];

const PAGE_SIZE_DEFAULT = 20;

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapResponse(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  return {
    items: pick(data, 'items', 'Items') ?? [],
    total: pick(data, 'total', 'Total') ?? 0,
    unreadCount: pick(data, 'unreadCount', 'UnreadCount') ?? 0,
    readCount: pick(data, 'readCount', 'ReadCount') ?? 0,
  };
}

const TYPE_TONES = {
  blue: { color: '#1d4ed8', background: 'rgba(0, 115, 234, 0.10)', borderColor: 'rgba(0, 115, 234, 0.32)' },
  green: { color: '#0f5132', background: 'rgba(0, 200, 117, 0.12)', borderColor: 'rgba(0, 200, 117, 0.30)' },
  orange: { color: '#92400e', background: 'rgba(253, 171, 61, 0.14)', borderColor: 'rgba(253, 171, 61, 0.32)' },
  purple: { color: '#5b21b6', background: 'rgba(139, 92, 246, 0.12)', borderColor: 'rgba(139, 92, 246, 0.32)' },
  teal: { color: '#0f766e', background: 'rgba(13, 148, 136, 0.12)', borderColor: 'rgba(13, 148, 136, 0.32)' },
  gray: { color: '#475569', background: 'rgba(100, 116, 139, 0.12)', borderColor: 'rgba(100, 116, 139, 0.28)' },
};

function typePill(type) {
  const meta = TYPE_META[type] || { label: type || '-', tone: 'gray' };
  const t = TYPE_TONES[meta.tone] || TYPE_TONES.gray;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', borderRadius: 999, fontWeight: 800, fontSize: 12,
      border: `1px solid ${t.borderColor}`, background: t.background, color: t.color,
      whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: 'currentColor', opacity: 0.85 }} />
      {meta.label}
    </span>
  );
}

function readPill(isRead) {
  const t = isRead
    ? { color: '#475569', background: 'rgba(100, 116, 139, 0.12)', borderColor: 'rgba(100, 116, 139, 0.28)', label: 'Okundu' }
    : { color: '#1d4ed8', background: 'rgba(0, 115, 234, 0.10)', borderColor: 'rgba(0, 115, 234, 0.32)', label: 'Yeni' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '3px 9px', borderRadius: 999, fontWeight: 800, fontSize: 11.5,
      border: `1px solid ${t.borderColor}`, background: t.background, color: t.color,
    }}>
      {!isRead && <span style={{ width: 6, height: 6, borderRadius: 999, background: 'currentColor' }} />}
      {t.label}
    </span>
  );
}

function formatDate(value) {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return '-'; }
}

function toIsoStart(dateStr) {
  if (!dateStr) return '';
  try { return new Date(`${dateStr}T00:00:00`).toISOString(); } catch { return ''; }
}

function toIsoEnd(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(`${dateStr}T00:00:00`);
    d.setDate(d.getDate() + 1);
    return d.toISOString();
  } catch { return ''; }
}

function badge(label, value, tone) {
  const tones = {
    blue: { bg: 'rgba(0, 115, 234, 0.10)', border: 'rgba(0, 115, 234, 0.32)', color: '#1d4ed8', dot: 'var(--accent)' },
    gray: { bg: 'rgba(100, 116, 139, 0.10)', border: 'rgba(100, 116, 139, 0.28)', color: '#475569', dot: '#64748b' },
  };
  const t = tones[tone] || tones.blue;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: '7px 12px', borderRadius: 999,
      background: t.bg, border: `1px solid ${t.border}`, color: t.color,
      fontWeight: 800, fontSize: 12,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: t.dot }} />
      {label}: {value}
    </span>
  );
}

export function NotificationsPageLegacy() {
  const [typeFilter, setTypeFilter] = useState('');
  const [readFilter, setReadFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchText, setSearchText] = useState('');

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ total: 0, unreadCount: 0, readCount: 0 });
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [sortField, setSortField] = useState('CreatedDate');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedId, setSelectedId] = useState(null);
  const [detailRow, setDetailRow] = useState(null);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (typeFilter) params.set('type', typeFilter);
      if (readFilter) params.set('isRead', readFilter);
      const isoFrom = toIsoStart(dateFrom);
      const isoTo = toIsoEnd(dateTo);
      if (isoFrom) params.set('from', isoFrom);
      if (isoTo) params.set('to', isoTo);
      params.set('take', '500');

      const res = await fetch(`${API_BASE}?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const data = unwrapResponse(payload);
      setRows(Array.isArray(data.items) ? data.items : []);
      setCounts({ total: data.total, unreadCount: data.unreadCount, readCount: data.readCount });
      setPage(1);
    } catch (err) {
      showToast('error', err.message || 'Liste alınamadı');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [typeFilter, readFilter, dateFrom, dateTo, showToast]);

  useEffect(() => { load(); }, [load]);

  const filteredSorted = useMemo(() => {
    const needle = searchText.trim().toLocaleLowerCase('tr-TR');
    const match = (row) => {
      if (!needle) return true;
      const fields = [
        pick(row, 'userName', 'UserName'),
        pick(row, 'userEmail', 'UserEmail'),
        pick(row, 'title', 'Title'),
        pick(row, 'body', 'Body'),
        pick(row, 'pointName', 'PointName'),
      ];
      return fields.some(v => v && String(v).toLocaleLowerCase('tr-TR').includes(needle));
    };
    const filtered = rows.filter(match);
    const dir = sortDir === 'desc' ? -1 : 1;
    const sortKey = (row) => {
      switch (sortField) {
        case 'UserName': return pick(row, 'userName', 'UserName') || '';
        case 'Type': return pick(row, 'type', 'Type') || '';
        case 'Title': return pick(row, 'title', 'Title') || '';
        case 'CreatedDate':
        default: {
          const v = pick(row, 'createdDate', 'CreatedDate');
          return v ? new Date(v).getTime() : 0;
        }
      }
    };
    return [...filtered].sort((a, b) => {
      const av = sortKey(a);
      const bv = sortKey(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [rows, searchText, sortField, sortDir]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredSorted.slice(start, start + pageSize);
  }, [filteredSorted, page, pageSize]);

  const columns = useMemo(() => [
    {
      field: 'createdDate',
      headerName: 'Tarih',
      flex: 0.9,
      minWidth: 150,
      valueGetter: ({ row }) => formatDate(pick(row, 'createdDate', 'CreatedDate')),
    },
    {
      field: 'userName',
      headerName: 'Kullanıcı',
      flex: 1.1,
      minWidth: 170,
      renderCell: ({ row }) => {
        const name = pick(row, 'userName', 'UserName');
        const email = pick(row, 'userEmail', 'UserEmail');
        return (
          <div>
            <div style={{ fontWeight: 600 }}>{name || `#${pick(row, 'userId', 'UserId')}`}</div>
            {email && <div style={{ fontSize: 11, color: '#6b7280' }}>{email}</div>}
          </div>
        );
      },
    },
    {
      field: 'type',
      headerName: 'Tip',
      flex: 0.7,
      minWidth: 130,
      renderCell: ({ row }) => typePill(pick(row, 'type', 'Type')),
    },
    {
      field: 'title',
      headerName: 'Başlık',
      flex: 1.5,
      minWidth: 220,
      renderCell: ({ row }) => {
        const title = pick(row, 'title', 'Title');
        const body = pick(row, 'body', 'Body');
        return (
          <div>
            <div style={{ fontWeight: 600 }}>{title || '-'}</div>
            {body && <div style={{ fontSize: 11.5, color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 320 }}>{body}</div>}
          </div>
        );
      },
    },
    {
      field: 'isRead',
      headerName: 'Durum',
      flex: 0.5,
      minWidth: 90,
      renderCell: ({ row }) => readPill(pick(row, 'isRead', 'IsRead')),
    },
    {
      field: 'actions',
      headerName: 'Aksiyon',
      flex: 0.5,
      minWidth: 90,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <div onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            style={{ ...gridActionButton, background: 'var(--accent)' }}
            onClick={() => setDetailRow(row)}
          >
            Detay
          </button>
        </div>
      ),
    },
  ], []);

  const totalCount = filteredSorted.length;

  return (
    <div style={s.container}>
      <div style={s.pageHeader}>
        <div style={s.pageHeaderText}>
          <h1 style={s.pageTitle}>Bildirimler</h1>
          <p style={s.pageSubtitle}>
            Kullanıcılara gönderilen tüm uygulama-içi bildirimlerin (push history) admin
            gözetim listesi. Okundu/okunmadı durumu ve deep-link payload'ı dahil.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {badge('Toplam', counts.total, 'gray')}
          {badge('Okunmamış', counts.unreadCount, 'blue')}
          <button type="button" style={s.btnSecondary} onClick={load} disabled={loading}>
            {loading ? 'Yükleniyor...' : 'Yenile'}
          </button>
        </div>
      </div>

      <div style={s.toolbar}>
        <div style={s.toolbarLeft}>
          {TYPE_TABS.map(tab => (
            <button
              key={tab.key || 'all'}
              type="button"
              onClick={() => setTypeFilter(tab.key)}
              style={{ ...(typeFilter === tab.key ? s.btnPrimary : s.btnSecondary), padding: '6px 12px', fontSize: 13 }}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div style={s.toolbarRight}>
          {READ_TABS.map(tab => (
            <button
              key={tab.key || 'all-read'}
              type="button"
              onClick={() => setReadFilter(tab.key)}
              style={{ ...(readFilter === tab.key ? s.btnPrimary : s.btnSecondary), padding: '6px 12px', fontSize: 13 }}
            >
              {tab.label}
            </button>
          ))}
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            style={{ ...s.filterInput, width: 140 }}
            title="Başlangıç"
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            style={{ ...s.filterInput, width: 140 }}
            title="Bitiş"
          />
          <input
            type="search"
            value={searchText}
            onChange={(e) => { setSearchText(e.target.value); setPage(1); }}
            placeholder="Kullanıcı / başlık..."
            style={s.filterInput}
          />
        </div>
      </div>

      <StandardDataGrid
        rows={pagedRows}
        columns={columns}
        totalCount={totalCount}
        loading={loading}
        pageSize={pageSize}
        currentPage={page}
        onPageChange={setPage}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
        sortField={sortField}
        sortDir={sortDir}
        onSortChange={(field, dir) => { setSortField(field); setSortDir(dir); }}
        selectedId={selectedId}
        onRowClick={(row) => setSelectedId(pick(row, 'id', 'Id'))}
        onRowDoubleClick={(row) => setDetailRow(row)}
        exportFileName="bildirimler"
      />

      {detailRow && (
        <div style={s.overlay} onClick={() => setDetailRow(null)}>
          <div style={{ ...s.modal, maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>{pick(detailRow, 'title', 'Title') || 'Bildirim'}</h2>
              <button type="button" style={s.closeBtn} onClick={() => setDetailRow(null)}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Tip</label>
                  <div>{typePill(pick(detailRow, 'type', 'Type'))}</div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Durum</label>
                  <div>{readPill(pick(detailRow, 'isRead', 'IsRead'))}</div>
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Kullanıcı</label>
                  <div style={s.inputView}>
                    {pick(detailRow, 'userName', 'UserName') || `#${pick(detailRow, 'userId', 'UserId')}`}
                    {pick(detailRow, 'userEmail', 'UserEmail') ? ` (${pick(detailRow, 'userEmail', 'UserEmail')})` : ''}
                  </div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Gönderim</label>
                  <div style={s.inputView}>{formatDate(pick(detailRow, 'createdDate', 'CreatedDate'))}</div>
                </div>
              </div>
              {pick(detailRow, 'pointName', 'PointName') && (
                <div style={s.formGroup}>
                  <label style={s.label}>Nokta</label>
                  <div style={s.inputView}>{pick(detailRow, 'pointName', 'PointName')}</div>
                </div>
              )}
              <div style={s.formGroup}>
                <label style={s.label}>İçerik</label>
                <div style={s.textareaView}>{pick(detailRow, 'body', 'Body') || '-'}</div>
              </div>
              {pick(detailRow, 'readAt', 'ReadAt') && (
                <div style={s.formGroup}>
                  <label style={s.label}>Okunma Zamanı</label>
                  <div style={s.inputView}>{formatDate(pick(detailRow, 'readAt', 'ReadAt'))}</div>
                </div>
              )}
              {pick(detailRow, 'payloadJson', 'PayloadJson') && (
                <div style={s.formGroup}>
                  <label style={s.label}>Deep-link Payload</label>
                  <pre style={{
                    background: '#0f172a', color: '#e2e8f0', padding: 12, borderRadius: 8,
                    fontSize: 12, overflowX: 'auto', margin: 0,
                  }}>{pick(detailRow, 'payloadJson', 'PayloadJson')}</pre>
                </div>
              )}
            </div>
            <div style={s.modalFooter}>
              <button type="button" style={s.btnSecondary} onClick={() => setDetailRow(null)}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default NotificationsPageLegacy;
